import React, { useState, useMemo, useCallback } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    Button,
    TextField,
    InputAdornment,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    MenuItem,
    Card,
    CardContent,
    CardActions,
    ToggleButtonGroup,
    ToggleButton,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Avatar,
    Divider,
    FormControl,
    InputLabel,
    Select,
    useTheme,
    alpha,
    CircularProgress,
    Stack
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Router as ReceiverIcon,
    ViewModule as CardViewIcon,
    TableChart as TableViewIcon,
    ViewList as CompactViewIcon,
    DirectionsCar as VehicleIcon,
    LocationOff as NoVehicleIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Import hooks including useTranslation
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useApiCall, useMutation } from '../hooks/useApi';
import { api, receiverHelpers } from '../services/api';
import { useNotification } from '../components/common/GlobalNotifications';
import AdvancedDataTable from '../components/common/AdvancedDataTable';

// CRITICAL FIX: Safe formatter that ALWAYS returns a string
const safeFormatDateTime = (value) => {
    try {
        if (!value) return 'N/A';

        let date;
        if (value instanceof Date) {
            date = value;
        } else if (typeof value === 'string' || typeof value === 'number') {
            date = new Date(value);
        } else {
            console.error('Invalid date value type:', typeof value, value);
            return 'Invalid Date';
        }

        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }

        // Simple format to avoid locale issues
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
        console.error('Error formatting date:', error);
        return 'Error';
    }
};

// CRITICAL FIX: Ensure any value is renderable
const ensureString = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';

    // Critical check for Promises
    if (value && typeof value.then === 'function') {
        console.error('WARNING: Attempted to render a Promise!', value);
        return fallback;
    }

    if (typeof value === 'object') {
        console.error('WARNING: Attempted to render an object!', value);
        return fallback;
    }

    return fallback;
};

// Helper to extract error message from API response
const getErrorMessage = (error, fallback = 'Error desconocido') => {
    if (!error) return fallback;

    // PRIORITY 1: Check if error already has a message field (from handleApiError)
    if (error.message && typeof error.message === 'string') {
        return error.message;
    }

    // PRIORITY 2: Check axios response structure (error.response.data.detail)
    if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
            return detail;
        }
        if (Array.isArray(detail)) {
            return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
        }
    }

    // PRIORITY 3: Check for detail field directly
    if (error.detail && typeof error.detail === 'string') {
        return error.detail;
    }

    // PRIORITY 4: Check for error field
    if (error.error && typeof error.error === 'string') {
        return error.error;
    }

    // PRIORITY 5: If error is a string itself
    if (typeof error === 'string') {
        return error;
    }

    // Log unhandled error structure for debugging
    console.error('Unhandled error structure:', error);

    return fallback;
};

// Simple Loading Component
const SimpleLoading = ({ message = 'Cargando...' }) => (
    <Container maxWidth={false} disableGutters>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="h6" color="text.secondary">
                {message}
            </Typography>
        </Box>
    </Container>
);

// MAC Address formatter
const formatMACAddress = (value) => {
    try {
        return receiverHelpers.formatMacAddress(value);
    } catch (e) {
        return ensureString(value);
    }
};

const flattenVehicleTree = (vehicleList = [], depth = 0) => {
    if (!Array.isArray(vehicleList)) return [];

    return vehicleList.flatMap((vehicle) => {
        const currentVehicle = {
            ...vehicle,
            nestingDepth: depth,
        };

        return [
            currentVehicle,
            ...flattenVehicleTree(vehicle.child_vehicles || [], depth + 1),
        ];
    });
};

// Receivers Card View Component
const ReceiversCardView = ({ receivers, vehicles, onEdit, onDelete, hasPermission, t }) => {
    const getVehicleName = (vehicleId) => {
        if (!vehicleId) return t('receivers.card.noAssigned', 'No asignado');
        const vehicle = vehicles?.find(v => v.id === vehicleId);
        if (!vehicle) return t('receivers.card.vehicleNotFound', 'Vehículo no encontrado');
        return ensureString(vehicle.display_name || vehicle.alias || `Vehículo ${vehicleId.substring(0, 8)}`);
    };

    return (
        <Grid container spacing={3}>
            {receivers.map((receiver) => (
                <Grid item xs={12} sm={6} md={4} key={receiver.id}>
                    <Card
                        sx={{
                            height: '100%',
                            transition: 'all 0.2s',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 4
                            }
                        }}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                                    <ReceiverIcon />
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="h6" noWrap fontWeight={600} fontFamily="monospace">
                                        {ensureString(receiver.mac_address)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {ensureString(receiver.dynamo_receiver_id)}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" fontWeight={600} gutterBottom>
                                    {t('receivers.card.vehicleAssignment', 'Asignación de Vehículo')}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {receiver.vehicle_id ? (
                                        <Chip
                                            icon={<VehicleIcon />}
                                            label={getVehicleName(receiver.vehicle_id)}
                                            size="small"
                                            color="primary"
                                            variant="filled"
                                        />
                                    ) : (
                                        <Chip
                                            icon={<NoVehicleIcon />}
                                            label={t('receivers.card.noAssigned', 'No asignado')}
                                            size="small"
                                            color="default"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                            </Box>

                            <Typography variant="caption" color="text.secondary">
                                {t('receivers.card.created', 'Creado')}: {safeFormatDateTime(receiver.created_at)}
                            </Typography>
                        </CardContent>

                        {hasPermission('manager') && (onEdit || onDelete) && (
                            <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {onEdit && (
                                        <Tooltip title={t('receivers.actions.editReceiver', 'Editar receptor')}>
                                            <IconButton size="small" onClick={() => onEdit(receiver)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {onDelete && (
                                        <Tooltip title={t('receivers.actions.deleteReceiver', 'Eliminar receptor')}>
                                            <IconButton size="small" color="error" onClick={() => onDelete(receiver)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </CardActions>
                        )}
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

// Compact List View Component
const CompactListView = ({ receivers, vehicles, onEdit, onDelete, hasPermission, t }) => {
    const getVehicleName = (vehicleId) => {
        if (!vehicleId) return t('receivers.card.noAssigned', 'No asignado');
        const vehicle = vehicles?.find(v => v.id === vehicleId);
        if (!vehicle) return t('receivers.card.vehicleNotFound', 'Vehículo no encontrado');
        return ensureString(vehicle.display_name || vehicle.alias || `Vehículo ${vehicleId.substring(0, 8)}`);
    };

    return (
        <Paper>
            <List>
                {receivers.map((receiver, index) => (
                    <React.Fragment key={receiver.id}>
                        <ListItem
                            secondaryAction={
                                hasPermission('manager') && (
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {onEdit && (
                                            <IconButton size="small" onClick={() => onEdit(receiver)}>
                                                <EditIcon />
                                            </IconButton>
                                        )}
                                        {onDelete && (
                                            <IconButton size="small" color="error" onClick={() => onDelete(receiver)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                )
                            }
                        >
                            <ListItemIcon>
                                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                    <ReceiverIcon />
                                </Avatar>
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body1" fontWeight={600}>
                                            {ensureString(receiver.mac_address)}
                                        </Typography>
                                        {receiver.vehicle_id ? (
                                            <Chip
                                                icon={<VehicleIcon />}
                                                label={t('receivers.card.assigned', 'Asignado')}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ) : (
                                            <Chip
                                                icon={<NoVehicleIcon />}
                                                label={t('receivers.stats.unassigned', 'Sin asignar')}
                                                size="small"
                                                color="default"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                }
                                secondary={`${ensureString(receiver.dynamo_receiver_id)} • ${getVehicleName(receiver.vehicle_id)}`}
                            />
                        </ListItem>
                        {index < receivers.length - 1 && <Divider />}
                    </React.Fragment>
                ))}
            </List>
        </Paper>
    );
};

const ReceiversPage = () => {
    const theme = useTheme();
    const { hasPermission } = useAuth();
    const { showSuccess, showError } = useNotification();
    const { t: originalT, ready: i18nReady } = useTranslation();

    // CRITICAL: Safe translation wrapper that ALWAYS returns a string
    const t = useCallback((key, fallback = null) => {
        try {
            // If translations aren't ready, return fallback
            if (!i18nReady) {
                return fallback || key.split('.').pop() || key;
            }

            // Get translation
            const result = originalT(key);

            // Check if it's a Promise (this is the critical issue)
            if (result && typeof result === 'object' && typeof result.then === 'function') {
                console.warn(`Translation returned Promise for key: ${key}`);
                return fallback || key.split('.').pop() || key;
            }

            // Ensure it's a string
            if (typeof result === 'string') {
                return result;
            }

            // Fallback if not string
            console.warn(`Translation returned non-string for key: ${key}`, typeof result);
            return fallback || key.split('.').pop() || key;
        } catch (error) {
            console.error(`Translation error for key: ${key}`, error);
            return fallback || key.split('.').pop() || key;
        }
    }, [i18nReady, originalT]);

    // State
    const [viewMode, setViewMode] = useState('table');
    const [searchReceivers, setSearchReceivers] = useState('');
    const [receiverDialog, setReceiverDialog] = useState({ open: false, receiver: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, receiver: null });
    const [assignmentFilter, setAssignmentFilter] = useState('all');
    const [receiverEntries, setReceiverEntries] = useState([{ mac_address: '' }]);
    const [receiverVehicleId, setReceiverVehicleId] = useState('');
    const [receiverBatchError, setReceiverBatchError] = useState(null);
    const [creatingReceivers, setCreatingReceivers] = useState(false);

    // Sorting state
    const orderBy = 'mac_address';
    const order = 'asc';

    // API calls
    const {
        data: receivers,
        loading: receiversLoading,
        refetch: refetchReceivers
    } = useApiCall(api.receivers.getAll);

    const {
        data: vehicles,
        loading: vehiclesLoading
    } = useApiCall(api.vehicles.getAll);

    // Create validation schema with safe translations
    const receiverSchema = useMemo(() => {
        return Yup.object({
            mac_address: Yup.string()
                .required(t('receivers.form.macAddressRequired', 'Dirección MAC requerida'))
                .matches(/^([0-9A-Fa-f]{2}[:]){5}([0-9A-Fa-f]{2})$/,
                    t('receivers.form.macAddressInvalid', 'Formato de dirección MAC inválido')),
            vehicle_id: Yup.string().nullable(),
        });
    }, [t]);

    // Receiver Mutations
    const { mutate: updateReceiver, loading: updating } = useMutation(
        ({ id, data }) => api.receivers.update(id, data),
        {
            onSuccess: () => {
                showSuccess(t('receivers.actions.receiverUpdated', 'Receptor actualizado'));
                setReceiverDialog({ open: false, receiver: null });
                refetchReceivers();
            },
            onError: (error) => {
                const errorMessage = getErrorMessage(error, t('hardcoded.pages_receiverspage.error_al_actualizar_receptor'));
                showError(errorMessage);
            }
        }
    );

    const { mutate: deleteReceiver, loading: deleting } = useMutation(api.receivers.delete, {
        onSuccess: () => {
            showSuccess(t('receivers.actions.receiverDeleted', 'Receptor eliminado'));
            setDeleteDialog({ open: false, receiver: null });
            refetchReceivers();
        },
        onError: (error) => {
            const errorMessage = getErrorMessage(error, t('hardcoded.pages_receiverspage.error_al_eliminar_receptor'));
            showError(errorMessage);
        }
    });

    // Receiver form
    const receiverFormik = useFormik({
        initialValues: {
            mac_address: '',
            dynamo_receiver_id: '',
            vehicle_id: '',
        },
        validationSchema: receiverSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            const submitData = {
                mac_address: values.mac_address,
                vehicle_id: values.vehicle_id || null
            };

            updateReceiver({ id: receiverDialog.receiver.id, data: submitData });
        },
    });

    // Auto-format MAC address
    const handleMACChange = (e) => {
        const formatted = formatMACAddress(e.target.value);
        receiverFormik.setFieldValue('mac_address', formatted);

        if (formatted && receiverHelpers.validateMacAddress(formatted)) {
            const dynamoId = receiverHelpers.macToDynamoId(formatted);
            receiverFormik.setFieldValue('dynamo_receiver_id', dynamoId);
        }
    };

    const handleReceiverEntryChange = (index, value) => {
        setReceiverBatchError(null);
        const formatted = formatMACAddress(value);
        setReceiverEntries((previous) => previous.map((entry, entryIndex) => (
            entryIndex === index ? { mac_address: formatted } : entry
        )));
    };

    const handleAddReceiverEntry = () => {
        setReceiverBatchError(null);
        setReceiverEntries((previous) => [...previous, { mac_address: '' }]);
    };

    const handleRemoveReceiverEntry = (index) => {
        setReceiverBatchError(null);
        setReceiverEntries((previous) => {
            if (previous.length === 1) {
                return previous;
            }
            return previous.filter((_, entryIndex) => entryIndex !== index);
        });
    };

    const resetReceiverCreateState = () => {
        setReceiverEntries([{ mac_address: '' }]);
        setReceiverVehicleId('');
        setReceiverBatchError(null);
    };

    const closeReceiverDialog = () => {
        setReceiverDialog({ open: false, receiver: null });
        receiverFormik.resetForm();
        resetReceiverCreateState();
    };

    const handleCreateReceiversSubmit = async (event) => {
        event.preventDefault();
        setReceiverBatchError(null);

        const receiversToCreate = receiverEntries
            .map((entry) => ({ mac_address: ensureString(entry.mac_address).trim() }))
            .filter((entry) => entry.mac_address);

        if (!receiversToCreate.length) {
            setReceiverBatchError(t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.agrega_al_menos_un_receptor'));
            return;
        }

        const invalidReceiver = receiversToCreate.find((entry) => !receiverHelpers.validateMacAddress(entry.mac_address));
        if (invalidReceiver) {
            setReceiverBatchError(`La MAC ${invalidReceiver.mac_address} no tiene un formato valido.`);
            return;
        }

        const normalizedMacs = receiversToCreate.map((entry) => entry.mac_address.toUpperCase());
        if (new Set(normalizedMacs).size !== normalizedMacs.length) {
            setReceiverBatchError(t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.no_puedes_registrar_macs_repetidas'));
            return;
        }

        setCreatingReceivers(true);
        try {
            for (const entry of receiversToCreate) {
                await api.receivers.create({
                    mac_address: entry.mac_address,
                    vehicle_id: receiverVehicleId || null,
                });
            }

            showSuccess(
                receiversToCreate.length === 1
                    ? t('receivers.actions.receiverCreated', 'Receptor creado exitosamente')
                    : t("hardcoded.pages_receiverspage.receptores_creados", { count: receiversToCreate.length })
            );
            closeReceiverDialog();
            await refetchReceivers();
        } catch (error) {
            const errorMessage = getErrorMessage(error, t('hardcoded.pages_receiverspage.error_al_crear_receptores'));
            setReceiverBatchError(errorMessage);
            showError(errorMessage);
        } finally {
            setCreatingReceivers(false);
        }
    };

    // Handle receiver dialog
    const handleReceiverDialog = (receiver = null) => {
        setReceiverDialog({ open: true, receiver });
        if (receiver) {
            receiverFormik.setValues({
                mac_address: receiver.mac_address,
                dynamo_receiver_id: receiver.dynamo_receiver_id,
                vehicle_id: receiver.vehicle_id || '',
            });
        } else {
            receiverFormik.resetForm();
            resetReceiverCreateState();
        }
    };

    // Handle delete
    const handleDelete = (receiver) => {
        setDeleteDialog({ open: true, receiver });
    };

    const confirmDelete = () => {
        if (deleteDialog.receiver) {
            deleteReceiver(deleteDialog.receiver.id);
        }
    };

    // Flatten vehicles for dropdown and lookups (includes child vehicles)
    const flatVehicles = useMemo(() => flattenVehicleTree(vehicles), [vehicles]);

    // Get vehicle name helper - ALWAYS RETURNS STRING
    const getVehicleName = useCallback((vehicleId, backendVehicleName = null) => {
        if (!vehicleId) return t('receivers.card.noAssigned', 'No asignado');

        const vehicle = flatVehicles?.find(v => `${v.id}` === `${vehicleId}`);
        if (!vehicle) {
            return ensureString(backendVehicleName, t('receivers.card.vehicleNotFound', 'Vehículo no encontrado'));
        }

        const displayName = vehicle.display_name || vehicle.alias || `Vehículo ${`${vehicleId}`.substring(0, 8)}`;
        return ensureString(displayName);
    }, [flatVehicles, t]);

    const descendingComparator = useCallback((a, b, currentOrderBy) => {
        let aValue;
        let bValue;

        if (currentOrderBy === 'vehicle') {
            aValue = getVehicleName(a.vehicle_id, a.vehicle_name);
            bValue = getVehicleName(b.vehicle_id, b.vehicle_name);
        } else if (currentOrderBy === 'status') {
            aValue = a.vehicle_id ? 'assigned' : 'unassigned';
            bValue = b.vehicle_id ? 'assigned' : 'unassigned';
        } else if (currentOrderBy === 'created_at') {
            aValue = new Date(a.created_at).getTime();
            bValue = new Date(b.created_at).getTime();
        } else {
            aValue = a[currentOrderBy] || '';
            bValue = b[currentOrderBy] || '';
        }

        if (bValue < aValue) {
            return -1;
        }
        if (bValue > aValue) {
            return 1;
        }
        return 0;
    }, [getVehicleName]);

    const getComparator = useCallback((currentOrder, currentOrderBy) => {
        return currentOrder === 'desc'
            ? (a, b) => descendingComparator(a, b, currentOrderBy)
            : (a, b) => -descendingComparator(a, b, currentOrderBy);
    }, [descendingComparator]);

    // Filter and sort receivers
    const filteredAndSortedReceivers = React.useMemo(() => {
        let filtered = receivers?.filter(receiver =>
            receiver.mac_address.toLowerCase().includes(searchReceivers.toLowerCase()) ||
            receiver.dynamo_receiver_id.toLowerCase().includes(searchReceivers.toLowerCase()) ||
            (receiver.vehicle_name && receiver.vehicle_name.toLowerCase().includes(searchReceivers.toLowerCase()))
        ) || [];

        if (assignmentFilter === 'assigned') {
            filtered = filtered.filter((receiver) => !!receiver.vehicle_id);
        }
        if (assignmentFilter === 'unassigned') {
            filtered = filtered.filter((receiver) => !receiver.vehicle_id);
        }

        return filtered.sort(getComparator(order, orderBy));
    }, [receivers, searchReceivers, assignmentFilter, order, orderBy, getComparator]);

    // View mode options
    const viewModeOptions = [
        { value: 'table', label: t('hardcoded.pages_receiverspage.view_table'), icon: <TableViewIcon /> },
        { value: 'cards', label: t('hardcoded.pages_receiverspage.view_cards'), icon: <CardViewIcon /> },
        { value: 'compact', label: t('hardcoded.pages_receiverspage.view_compact'), icon: <CompactViewIcon /> }
    ];

    // Show loading
    if (receiversLoading || vehiclesLoading) {
        return <SimpleLoading message={t('receivers.loadingMessage', 'Cargando receptores...')} />;
    }

    // Render content based on view mode
    const renderReceiversContent = () => {
        if (filteredAndSortedReceivers.length === 0) {
            return (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <ReceiverIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {t('receivers.noReceiversFound', 'No se encontraron receptores')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {searchReceivers ?
                            t('receivers.adjustCriteria', 'Intenta ajustar los criterios de búsqueda') :
                            t('receivers.registerFirst', 'Registra tu primer receptor')}
                    </Typography>
                </Paper>
            );
        }

        switch (viewMode) {
            case 'cards':
                return (
                    <ReceiversCardView
                        receivers={filteredAndSortedReceivers}
                        vehicles={flatVehicles}
                        onEdit={hasPermission('manager') ? handleReceiverDialog : undefined}
                        onDelete={hasPermission('manager') ? handleDelete : undefined}
                        hasPermission={hasPermission}
                        t={t}
                    />
                );
            case 'compact':
                return (
                    <CompactListView
                        receivers={filteredAndSortedReceivers}
                        vehicles={flatVehicles}
                        onEdit={hasPermission('manager') ? handleReceiverDialog : undefined}
                        onDelete={hasPermission('manager') ? handleDelete : undefined}
                        hasPermission={hasPermission}
                        t={t}
                    />
                );
            default: // table
                return (
                    <AdvancedDataTable
                        storageKey="receivers_table"
                        rows={filteredAndSortedReceivers}
                        rowKey="id"
                        initialSortBy="mac_address"
                        columns={[
                            {
                                id: 'mac_address',
                                label: t('receivers.table.macAddress', 'Dirección MAC'),
                                accessor: (row) => row.mac_address,
                                filterable: true,
                                render: (row) => (
                                    <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                        {ensureString(row.mac_address)}
                                    </span>
                                )
                            },
                            {
                                id: 'dynamo_receiver_id',
                                label: t('receivers.table.dynamoId', 'ID'),
                                accessor: (row) => row.dynamo_receiver_id,
                                filterable: true,
                                render: (row) => (
                                    <span style={{ fontFamily: 'monospace' }}>
                                        {ensureString(row.dynamo_receiver_id)}
                                    </span>
                                )
                            },
                            {
                                id: 'vehicle_name',
                                label: t('receivers.table.assignedVehicle', 'Vehículo Asignado'),
                                accessor: (row) => getVehicleName(row.vehicle_id, row.vehicle_name),
                                filterable: true
                            },
                            {
                                id: 'status',
                                label: t('receivers.table.status', 'Estado'),
                                accessor: (row) => (row.vehicle_id ? t('hardcoded.pages_receiverspage.asignado') : t('sensors.stats.unassigned')),
                                filterable: true,
                                filterType: 'select',
                                filterOptions: [
                                    { value: 'Asignado', label: t('receivers.card.assigned', 'Asignado') },
                                    { value: t('sensors.stats.unassigned'), label: t('receivers.stats.unassigned', 'Sin asignar') }
                                ],
                                render: (row) => row.vehicle_id ? (
                                    <Chip label={t('receivers.card.assigned', 'Asignado')} size="small" color="primary" variant="outlined" />
                                ) : (
                                    <Chip label={t('receivers.stats.unassigned', 'Sin asignar')} size="small" color="default" variant="outlined" />
                                )
                            },
                            {
                                id: 'last_update_at',
                                label: t('receivers.table.lastUpdate', 'Última actualización'),
                                accessor: (row) => row.last_update_at || '',
                                filterable: true,
                                render: (row) => row.last_update_at
                                    ? safeFormatDateTime(row.last_update_at)
                                    : t('receivers.table.noLastUpdate', 'Sin actualización registrada')
                            },
                            {
                                id: 'created_at',
                                label: t('receivers.table.creationDate', 'Fecha de Creación'),
                                accessor: (row) => row.created_at,
                                filterable: true,
                                render: (row) => safeFormatDateTime(row.created_at)
                            }
                        ]}
                        rowActions={hasPermission('manager') ? (receiver) => (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                <Tooltip title={t('receivers.actions.editReceiver', 'Editar receptor')}>
                                    <IconButton size="small" onClick={() => handleReceiverDialog(receiver)}>
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('receivers.actions.deleteReceiver', 'Eliminar receptor')}>
                                    <IconButton size="small" color="error" onClick={() => handleDelete(receiver)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ) : undefined}
                        emptyTitle={t('receivers.noReceiversFound', 'No se encontraron receptores')}
                        emptyDescription={t('receivers.adjustCriteria', 'Intenta ajustar los criterios de búsqueda')}
                    />
                );
        }
    };

    return (
        <Container maxWidth={false} disableGutters>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                    {t('receivers.title', 'Gestión de Receptores')}
                </Typography>
            </Box>

            {/* Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        placeholder={t('receivers.search', 'Buscar receptores...')}
                        value={searchReceivers}
                        onChange={(e) => setSearchReceivers(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        size="small"
                        sx={{ width: { xs: '100%', sm: 300 } }}
                    />

                    <Tooltip title={t('common.refresh', 'Actualizar')}>
                        <IconButton
                            onClick={refetchReceivers}
                            size="small"
                            color="primary"
                            sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>

                    {/* View Mode Toggle */}
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, newMode) => newMode && setViewMode(newMode)}
                        size="small"
                    >
                        {viewModeOptions.map((option) => (
                            <ToggleButton key={option.value} value={option.value}>
                                <Tooltip title={option.label}>
                                    {option.icon}
                                </Tooltip>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                {hasPermission('manager') && (
                    <Button
                        data-tutorial-id="add-receiver-btn"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleReceiverDialog()}
                        size="large"
                    >
                        {t('receivers.registerReceiver', 'Registrar Receptor')}
                    </Button>
                )}
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 2, cursor: 'pointer' }} onClick={() => setAssignmentFilter('all')}>
                            <Typography variant="h4" fontWeight={700} color="secondary.main">
                                {receivers?.length || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('receivers.stats.totalReceivers', 'Total de Receptores')}
                            </Typography>
                            {assignmentFilter === 'all' && <Chip size="small" color="secondary" label={t('common.active', 'Activo')} sx={{ mt: 1 }} />}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 2, cursor: 'pointer' }} onClick={() => setAssignmentFilter('assigned')}>
                            <Typography variant="h4" fontWeight={700} color="success.main">
                                {receivers?.filter(r => r.vehicle_id).length || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('receivers.stats.assigned', 'Asignados')}
                            </Typography>
                            {assignmentFilter === 'assigned' && <Chip size="small" color="success" label={t('common.active', 'Activo')} sx={{ mt: 1 }} />}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 2, cursor: 'pointer' }} onClick={() => setAssignmentFilter('unassigned')}>
                            <Typography variant="h4" fontWeight={700} color="warning.main">
                                {receivers?.filter(r => !r.vehicle_id).length || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('receivers.stats.unassigned', 'Sin Asignar')}
                            </Typography>
                            {assignmentFilter === 'unassigned' && <Chip size="small" color="warning" label={t('common.active', 'Activo')} sx={{ mt: 1 }} />}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" fontWeight={700} color="info.main">
                                {vehicles?.length || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('receivers.stats.availableVehicles', 'Vehículos Disponibles')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Content */}
            {renderReceiversContent()}

            {/* Receiver Dialog */}
            <Dialog
                open={receiverDialog.open}
                onClose={closeReceiverDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    'data-tutorial-id': 'receiver-form-dialog',
                }}
            >
                <form onSubmit={receiverDialog.receiver ? receiverFormik.handleSubmit : handleCreateReceiversSubmit}>
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ReceiverIcon color="primary" />
                            {receiverDialog.receiver ?
                                t('receivers.form.editTitle', 'Editar Receptor') :
                                t('receivers.form.createTitle', 'Registrar Nuevo Receptor')}
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            {receiverDialog.receiver ? (
                                <>
                                    <TextField
                                        fullWidth
                                        label={t('receivers.form.macAddress', 'Dirección MAC')}
                                        name="mac_address"
                                        value={receiverFormik.values.mac_address}
                                        onChange={handleMACChange}
                                        onBlur={receiverFormik.handleBlur}
                                        error={receiverFormik.touched.mac_address && Boolean(receiverFormik.errors.mac_address)}
                                        helperText={
                                            (receiverFormik.touched.mac_address && ensureString(receiverFormik.errors.mac_address)) ||
                                            t('receivers.form.macAddressHelper', 'Formato: XX:XX:XX:XX:XX:XX')
                                        }
                                        placeholder="00:00:00:00:00:00"
                                        inputProps={{
                                            style: { fontFamily: 'monospace' },
                                            'data-tutorial-id': 'receiver-form-mac',
                                        }}
                                        sx={{ mb: 3 }}
                                    />

                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            {t('receivers.form.dynamoId', 'ID del Receptor')}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontFamily: 'monospace' }}
                                        >
                                            {ensureString(receiverFormik.values.dynamo_receiver_id, '—')}
                                        </Typography>
                                    </Box>

                                    <FormControl fullWidth>
                                        <InputLabel>{t('receivers.form.assignedVehicle', 'Vehículo Asignado')}</InputLabel>
                                        <Select
                                            name="vehicle_id"
                                            value={receiverFormik.values.vehicle_id}
                                            onChange={receiverFormik.handleChange}
                                            label={t('receivers.form.assignedVehicle', 'Vehículo Asignado')}
                                            inputProps={{
                                                'data-tutorial-id': 'receiver-form-vehicle',
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>{t('receivers.form.noAssignment', 'Sin asignación')}</em>
                                            </MenuItem>
                                            {flatVehicles?.map((vehicle) => (
                                                <MenuItem key={vehicle.id} value={vehicle.id}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <VehicleIcon fontSize="small" />
                                                        <Typography>
                                                            {`${'— '.repeat(vehicle.nestingDepth || 0)}${ensureString(vehicle.display_name || vehicle.alias || `Vehículo ${vehicle.id.substring(0, 8)}`)}`}
                                                        </Typography>
                                                        {vehicle.parent_vehicle_id && (
                                                            <Chip label={t('receivers.form.trailer', 'Remolque')} size="small" variant="outlined" />
                                                        )}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </>
                            ) : (
                                <Stack spacing={3}>
                                    {receiverBatchError && (
                                        <Alert severity="error">{receiverBatchError}</Alert>
                                    )}

                                    <Typography variant="body2" color="text.secondary">
                                        {t('hardcoded.pages_receiverspage.registra_varios_receptores_y_si')}</Typography>

                                    <FormControl fullWidth>
                                        <InputLabel>{t('receivers.form.assignedVehicle', 'Vehículo Asignado')}</InputLabel>
                                        <Select
                                            value={receiverVehicleId}
                                            onChange={(event) => {
                                                setReceiverBatchError(null);
                                                setReceiverVehicleId(event.target.value);
                                            }}
                                            label={t('receivers.form.assignedVehicle', 'Vehículo Asignado')}
                                        >
                                            <MenuItem value="">
                                                <em>{t('receivers.form.noAssignment', 'Sin asignación')}</em>
                                            </MenuItem>
                                            {flatVehicles?.map((vehicle) => (
                                                <MenuItem key={vehicle.id} value={vehicle.id}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <VehicleIcon fontSize="small" />
                                                        <Typography>
                                                            {`${'— '.repeat(vehicle.nestingDepth || 0)}${ensureString(vehicle.display_name || vehicle.alias || `Vehículo ${vehicle.id.substring(0, 8)}`)}`}
                                                        </Typography>
                                                        {vehicle.parent_vehicle_id && (
                                                            <Chip label={t('receivers.form.trailer', 'Remolque')} size="small" variant="outlined" />
                                                        )}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Stack spacing={2}>
                                        {receiverEntries.map((entry, index) => {
                                            const dynamoId = receiverHelpers.validateMacAddress(entry.mac_address)
                                                ? receiverHelpers.macToDynamoId(entry.mac_address)
                                                : '—';

                                            return (
                                                <Box
                                                    key={`receiver-entry-${index}`}
                                                    sx={{
                                                        p: 2,
                                                        border: 1,
                                                        borderColor: 'divider',
                                                        borderRadius: 2,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 2,
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Typography variant="subtitle2" fontWeight={600}>
                                                            Receptor {index + 1}
                                                        </Typography>
                                                        {receiverEntries.length > 1 && (
                                                            <IconButton color="error" onClick={() => handleRemoveReceiverEntry(index)}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        )}
                                                    </Box>

                                                    <TextField
                                                        fullWidth
                                                        label={t('receivers.form.macAddress', 'Dirección MAC')}
                                                        value={entry.mac_address}
                                                        onChange={(event) => handleReceiverEntryChange(index, event.target.value)}
                                                        placeholder="00:00:00:00:00:00"
                                                        inputProps={{ style: { fontFamily: 'monospace' } }}
                                                    />

                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                            {t('receivers.form.dynamoId', 'ID del Receptor')}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                            {ensureString(dynamoId, '—')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Stack>

                                    <Button type="button" variant="outlined" startIcon={<AddIcon />} onClick={handleAddReceiverEntry}>
                                        {t("hardcoded.pages_receiverspage.agregar_otro_receptor")}
                                    </Button>
                                </Stack>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeReceiverDialog}>
                            {t('receivers.form.cancel', 'Cancelar')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={creatingReceivers || updating}
                        >
                            {receiverDialog.receiver ?
                                t('receivers.form.update', 'Actualizar') :
                                t('receivers.form.register', 'Registrar')}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, receiver: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeleteIcon color="error" />
                        {t('receivers.actions.confirmDelete', 'Confirmar Eliminación')}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        {t('receivers.actions.deleteMessage', '¿Estás seguro de que quieres eliminar este receptor? Esta acción no se puede deshacer.')}
                    </Typography>
                    {deleteDialog.receiver && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                                {ensureString(deleteDialog.receiver.mac_address)} - {ensureString(deleteDialog.receiver.dynamo_receiver_id)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {getVehicleName(deleteDialog.receiver.vehicle_id, deleteDialog.receiver.vehicle_name)}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, receiver: null })}>
                        {t('common.cancel', 'Cancelar')}
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={confirmDelete}
                        disabled={deleting}
                        startIcon={<DeleteIcon />}
                    >
                        {t('common.delete', 'Eliminar')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ReceiversPage;